import React, { PureComponent } from 'react'
import Button from '../../../../Shared/Form/Button'
import Modal from '../../../../Shared/Modal'
import { getRoomName } from '../common'
import Alert from '../../../../Shared/Alert'
import Icon from '../../../../Shared/Icon'
import './style.scss'
import { deleteRoom, getFactsheetBlueprint } from '../../../../../api'
import Loader from '../../../../Shared/Loader'
import get from 'lodash.get'
import { getFactInstanceName } from '../../../../Shared/Factsheet/helper'
import PropTypes from 'prop-types'
import { Trans, withTranslation } from 'react-i18next'
import { SHAPE_ROOM } from '../shapes'
import HotelierPropertyService from '../../../../../services/hotelierPropertyService'
import activityTrackingService from '../../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../../constants'

const MAX_VISIBLE_IMAGES = 5
const MAX_VISIBLE_FACTS = 5

class DeleteRoomModal extends PureComponent {

  abortController = null
  state = {
    giataHotelierProperty: null,
    factTree: null,
    errorLoading: null,
    errorDeleting: null,
    loading: false,
    initialized: false
  }

  componentDidMount () {
    this.init()
  }

  componentWillUnmount () {
    if (this.abortController) {
      this.abortController.abort()
    }
  }

  init () {
    this.setState({
      errorLoading: null,
      errorDeleting: null,
      giataHotelierProperty: null,
      factsheet: null,
      loading: true
    }, async () => {
      if (this.abortController !== null) {
        this.abortController.abort()
      }
      this.abortController = new AbortController()
      const requests = []
      let request = HotelierPropertyService.getGiataHotelierProperty(this.props.giataId, this.abortController)
      requests.push(request)
      request = getFactsheetBlueprint(this.abortController)
      requests.push(request.fetchPromise)
      let responses
      try {
        responses = await Promise.all(requests)
      } catch (e) {
        if (this.abortController.signal.aborted) {
          return
        }
        this.setState({ loading: false, errorLoading: true, errorDeleting: null })
        return
      }
      if (responses[0].initialFactsheet) {
        responses[0].giataHotelierProperty.factsheet = responses[0].initialFactsheet
      }
      this.setState({
        giataHotelierProperty: responses[0].giataHotelierProperty,
        factTree: responses[1].factTree,
        loading: false,
        initialized: true
      })
    })
  }

  isHidingAllowed () {
    return !this.state.loading
  }

  onConfirmClick = () => {
    if (this.abortController) {
      this.abortController.abort()
    }
    activityTrackingService.trackEvent(trackingEvent.deleteRoom)
    this.abortController = new AbortController()
    this.setState({ loading: true, errorDeleting: false }, () => {
      const { fetchPromise } = deleteRoom(this.props.giataId, this.props.room.id, this.abortController)
      fetchPromise.then(() => {
        this.setState({ loading: false, errorDeleting: null }, () => {
          this.props.onRoomRemoved(this.props.room)
          const newGalleries = { ...this.props.galleries, rooms: { ...this.props.galleries.rooms } }
          delete newGalleries.rooms[this.props.room.id]
          this.props.onGalleriesChange(newGalleries, true)
        })
      }).catch(() => {
        if (this.abortController.signal.aborted) {
          return
        }
        this.setState({ loading: false, errorDeleting: true })
      })
    })
  }

  renderFacts () {
    let facts = get(this.state.giataHotelierProperty, 'factsheet.facts', {})
    const appliesToFactInstances = []
    Object.values(facts).forEach((factInstances) => {
      factInstances.forEach((factInstance, factInstanceKey) => {
        if (factInstance.hasOwnProperty('appliesTo') && factInstance.appliesTo.includes(this.props.room.id)) {
          appliesToFactInstances.push({ key: factInstanceKey, instance: factInstance, })
        }
      })
    })
    if (appliesToFactInstances.length === 0) {
      return <span className="delete-room-modal__facts-placeholder">{this.props.t('NO_FACTS_IN_ROOM')}</span>
    }
    const hasMoreFacts = appliesToFactInstances.length > MAX_VISIBLE_FACTS
    const visibleFacts = hasMoreFacts ? appliesToFactInstances.slice(0, MAX_VISIBLE_FACTS) : appliesToFactInstances
    const hiddenFactsCount = appliesToFactInstances.length - MAX_VISIBLE_FACTS
    return (
      <div className="delete-room-modal__facts-list">
        {
          visibleFacts.map((v) => {
            const defaultLabel = get(
              this.state.factTree,
              'facts.' + v.instance.factDefId + '.label', '-'
            )
            return (
              <span
                key={v.instance.factDefId + '-' + v.key}
                className="delete-room-modal__fact-item"
              >
                {getFactInstanceName(v.instance, v.key, defaultLabel)}
              </span>
            )
          })
        }
        {hasMoreFacts && (
          <span className="delete-room-modal__fact-more">
            {' '}({this.props.t('AND_MORE_FACTS', { count: hiddenFactsCount })})
          </span>
        )}
      </div>
    )
  }

  renderImages () {
    const images = this.getImagesRelatedToCurrentRoom()
    if (images.length === 0) {
      return <span className="delete-room-modal__images-placeholder">{this.props.t('NO_IMAGES_IN_ROOM')}</span>
    }
    const hasMore = images.length > MAX_VISIBLE_IMAGES
    const visibleImages = hasMore ? images.slice(0, MAX_VISIBLE_IMAGES) : images
    const hiddenCount = images.length - (MAX_VISIBLE_IMAGES - 1)
    return (
      <div className="delete-room-modal__thumbnails">
        {visibleImages.map((image, index) => {
          const isWithCount = hasMore && index === visibleImages.length - 1
          return (
            <div key={image.id} className="delete-room-modal__thumbnail-wrapper">
              <img
                className={`delete-room-modal__thumbnail ${isWithCount ? 'delete-room-modal__thumbnail--darkened' : ''}`}
                src={image.thumbnailUrl}
                title={image.filename}
                alt={isWithCount ? this.props.t('MORE_IMAGES_ALT') : image.filename}
              />
              {isWithCount && (
                <span className="delete-room-modal__more-count">+{hiddenCount}</span>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  onEscapeKeyDown = () => {
    this.isHidingAllowed() && this.props.onEscapeKeyDown()
  }

  onBackdropClick = () => {
    this.isHidingAllowed() && this.props.onBackdropClick()
  }

  onCancel = () => {
    this.isHidingAllowed() && this.props.onCancel()
  }

  getImagesRelatedToCurrentRoom () {
    return this.props.images.filter((image) => {
      return this.props.galleries?.rooms?.[this.props.room.id]?.order?.includes(image.id)
    })
  }

  render () {
    const { t } = this.props
    return (
      <Modal show={this.props.show}
             extraClassNames="delete-room-modal"
             onEscapeKeyDown={this.onEscapeKeyDown}
             onBackdropClick={this.onBackdropClick}>
        <div className="delete-room-modal__container">
          {this.state.loading && <Loader size={32}/>}
          {(this.state.errorDeleting || this.state.errorLoading) &&
            <Alert type={'danger'}>Error</Alert>
          }

          {this.state.initialized &&
            <React.Fragment>
              <div className="delete-room-modal__overview">
                <div className="delete-room-modal__icon-wrapper">
                  <Icon type="room-delete" background={'transparent'} extraClassNames="delete-room-modal__hero-icon"/>
                </div>
                <div className="delete-room-modal__details">
                  <h2 className="delete-room-modal__title">
                    <Trans
                      i18nKey="DELETE_ROOM_TITLE"
                      components={{ highlight: <strong className="delete-room-modal__highlight"/> }}
                    />
                  </h2>
                  <ul className="delete-room-modal__room-list">
                    <li className="delete-room-modal__room-item">
                      {getRoomName(this.props.roomAttributes, this.props.room)}
                    </li>
                  </ul>
                  <div className="delete-room-modal__images-summary">
                    <span className="delete-room-modal__images-label">{t('IMAGES_IN_THIS_ROOM')}:</span>
                    {this.renderImages()}
                  </div>
                  <div className="delete-room-modal__facts-summary">
                    <span className="delete-room-modal__facts-label">{t('FACTS_IN_THIS_ROOM')}:</span>
                    {this.renderFacts()}
                  </div>
                </div>
              </div>
              <div className="delete-room-modal__notice-card">
                <div className="delete-room-modal__notice-icon-wrapper">
                  <Icon type="warning-triangle" background={'transparent'}
                        extraClassNames="delete-room-modal__notice-icon"/>
                </div>
                <div className="delete-room-modal__notice-body">
                  <p className="delete-room-modal__notice-text">{t('DELETE_ROOM_IMPLICATION_WARNING')}</p>
                  <p className="delete-room-modal__notice-text">{t('DELETE_ROOM_IMPLICATION_NOTE')}</p>
                </div>
              </div>
              <div className="delete-room-modal__actions">
                <Button variant="attention"
                        classNames="delete-room-modal__action-button"
                        onClick={this.onConfirmClick}>{t('BUTTON_DELETE')}</Button>
                <Button variant="neutral"
                        classNames="delete-room-modal__action-button"
                        onClick={this.onCancel}>{t('BUTTON_CANCEL')}</Button>
              </div>
            </React.Fragment>
          }
        </div>
      </Modal>
    )
  }
}

DeleteRoomModal.propTypes = {
  show: PropTypes.bool.isRequired,
  giataId: PropTypes.number.isRequired,
  room: PropTypes.shape(SHAPE_ROOM).isRequired,
  roomAttributes: PropTypes.any.isRequired,
  images: PropTypes.array.isRequired,
  onEscapeKeyDown: PropTypes.func.isRequired,
  onBackdropClick: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onRoomRemoved: PropTypes.func.isRequired,
  galleries: PropTypes.object,
  onGalleriesChange: PropTypes.func,
}
export default withTranslation()(DeleteRoomModal)
